import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from "next";
import { useRouter } from "next/router";
import { useEffect } from "react";
import parlors from "@/constants/parlors_detail.json";
import parlors_discount from "@/constants/parlors_discount_detail.json";
import { getEskyPrice } from "@/utils/eskyPrice";
// ponytail: 2026-10-08 에 git 이력(parlors_detail.json 커밋 89개)에서 id 별 처음 들어간 날을 뽑은 고정 목록.
// 이후 추가되는 장례식장은 여기 없어서 datePublished 를 생략한다. 늘어나면 위키의 스크립트로 다시 뽑는다
import parlorPublishedDates from "@/constants/parlor_published_dates.json";

// import numberToLocalString from "@/utils/numberToLocalString";
import NotFoundPage from "pages/404";
import RichSearch from "@/components/common/RichSearch";
import { colors, FUNERAL_SIGUNGU, HLine } from "goi_common";
import { getThreeClosestParlorFrom } from "@/containers/Parlor/ParlorDetailContainer/utils/getThreeClosestParlorFrom";
import { getNearbyFacilities } from "@/containers/Parlor/ParlorDetailContainer/utils/getNearbyFacilities";
// import { richSearchData } from "@/constants/richSearch";
import { BinsoObject, ParlorDetailProps } from "@/types/parlor";
import Head from "next/head";
import { skipDevBuildInGetStaticPaths } from "@/utils/skipDevBuildInGetStaticPaths";

// import Footer from "@/components/Layout/Footer";
import { excludeParlors, safeParlorIds } from "@/constants/parlor/excludeParlors";
import { useOpenParlorPromotion50Modal } from "@/hooks/useParlor50PromotionModal";
import RenewalFooter from "@/containers/HomeContainer/components/RenewalFooter";
import { richSearchData } from "@/constants/richSearch";
import { ParlorDetailTableVersionContainer } from "@/containers/Parlor/ParlorDetailContainer";
import ParlorDetailContainerMayVersion from "@/containers/Parlor/ParlorDetailContainer/ParlorDetailContainerMayVersion";
import { markParlorRequestAdEntry } from "@/utils/parlorRequestAdEntry";
import {
  getParlorPriceDisclosure,
  isPriceDisclosureParlor,
  toFaqPageJsonLd,
} from "@/utils/parlorPriceDisclosure";

export const getStaticPaths: GetStaticPaths = async () => {
  const skipBuildResult = skipDevBuildInGetStaticPaths() as GetStaticPathsResult;
  if (skipBuildResult) {
    return skipBuildResult;
  }

  const paths = parlors
    // .filter((parlor) => !excludeParlors.includes(String(parlor.id)))
    .map((parlor) => ({
      params: {
        id: parlor.id.toString(),
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const id = params?.id;
  let parlor;
  let district;
  let name = "";
  let description = "";
  let closestParlorsByService = {};
  let discountDescription =
    parlors_discount.find((p) => p.parlor_id == Number(id))?.description || null;

  const isSafeParlor = safeParlorIds.includes(Number(id));

  if (id) {
    parlor = parlors.find((parlor) => parlor.id === parseInt(params?.id as string));
    if (parlor) {
      if (!discountDescription) {
        discountDescription = ["21", "26", "38"].includes(parlor.sido)
          ? "20%이상 할인받을 수 있어요"
          : null;
      }

      name = parlor.companyname;

      district = parlor.address.slice(0, 2);
      if (parlor.address.split(" ")[0].length == 4)
        district = parlor.address[0] + parlor.address[2];

      /**
       * 실제 검색 결과 설명문으로 그대로 쓰인다 (10/8 SERP 200곳 중 www 32건 중 31건).
       * 예전 문구는 줄바꿈과 "평형별 정보 ... 40평형 , 주소" 처럼 깨진 조각, 1,000곳 공통 문구가 섞여 있었다.
       * 장례식장마다 다른 숫자만 짧게 넣는다. 일반 템플릿은 빈소 사용료를 넣지 않는다
       * (가격 공개 실험 대조군인 홀수 id 화면에 없는 정보라 설명문에서 새면 안 된다).
       */
      const manwon = (won: number) => `${Math.round(won / 10000).toLocaleString()}만원`;
      const pyeongs = parlor.binsos
        .map((binso: BinsoObject) => Math.round(binso.pyeong ?? 0))
        .filter((pyeong: number) => pyeong > 0);
      const minCharge = Math.min(
        ...parlor.binsos.map((binso: BinsoObject) => binso.charge).filter((c: number) => c > 0),
      );
      const binsoText = pyeongs.length
        ? `빈소 ${parlor.binsos.length}곳(${Math.min(...pyeongs)}~${Math.max(...pyeongs)}평${
            isSafeParlor && Number.isFinite(minCharge) ? `, 1일 ${manwon(minCharge)}부터` : ""
          })`
        : "";
      const parkCnt = Number(parlor.amenities?.parkcnt) || 0;
      const sigunguName = FUNERAL_SIGUNGU.get(Number(parlor.sido))?.get(Number(parlor.sigungu));
      const charges = parlor.expected_charge_sum;

      description = [
        `${name}${sigunguName ? `(${sigunguName})` : ""} ${[
          binsoText,
          parkCnt ? `주차 ${parkCnt}대` : "",
        ]
          .filter(Boolean)
          .join(", ")}.`,
        `장례식장 비용 조문객이 없으면(무빈소) ${manwon(
          charges["무빈소"],
        )}, 조문객이 있으면 ${manwon(charges["가족장"])}~${manwon(charges["일반장"])}.`,
        parlor.address,
      ]
        .join(" ")
        .replace(/\s+\./, ".");

      closestParlorsByService = {
        // 8곳까지 (비교 마퀴용). 주변 장례식장 섹션은 렌더 시 3곳으로 slice
        무빈소: getThreeClosestParlorFrom(parlor as any, "무빈소", 8),
        가족장: getThreeClosestParlorFrom(parlor as any, "가족장", 8),
        일반장: getThreeClosestParlorFrom(parlor as any, "일반장", 8),
      };
    }
  }

  const nearbyFacilities = parlor ? getNearbyFacilities(parlor as any, 3) : [];

  const localBusinessSchema = parlor
    ? {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: name,
        description: `${name} 예상 비용, 빈소 정보, 시설 안내. 고이장례연구소에서 무료 견적 확인.`,
        url: `https://www.goifuneral.co.kr/parlor/${parlor.id}/`,
        telephone: parlor.telephone || null,
        address: {
          "@type": "PostalAddress",
          streetAddress: parlor.address,
          addressLocality:
            FUNERAL_SIGUNGU.get(Number(parlor.sido))?.get(Number(parlor.sigungu)) || null,
          addressCountry: "KR",
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: parlor.latitude,
          longitude: parlor.longitude,
        },
        image: parlor.images?.[0]?.url || null,
        priceRange: `${parlor.expected_charge_sum[
          "무빈소"
        ].toLocaleString()}원 ~ ${parlor.expected_charge_sum["일반장"].toLocaleString()}원`,
      }
    : null;

  return {
    props: {
      defaultParlor: parlor
        ? {
            ...parlor,
            esky_price: isPriceDisclosureParlor({
              id: String(parlor.id),
              is_excluded: parlor.is_excluded,
            })
              ? getEskyPrice(parlor.id)
              : null,
          }
        : parlor,
      localBusinessSchema,
      pageDescription: description,
      publishedDate: (parlorPublishedDates as Record<string, string>)[String(id)] ?? null,
      pageKeywords: `${name}, ${name} 빈소 정보, ${name} 비용, ${name} 위치, ${name} 정보, ${name} 정보 후기, 시설정보, ${district} 장례식장, ${name} 근조화환발송`,
      pageOgTitle: `${name}(${FUNERAL_SIGUNGU.get(Number(parlor?.sido))?.get(
        Number(parlor?.sigungu),
      )}) ${isSafeParlor ? "비용·빈소 정보" : "정보, 위치, 비용, 시설"} - 고이`,
      pageOgDescription: description,
      pageOgImage: parlor?.images[0]?.url || `${process.env.NEXT_PUBLIC_CDN_URL}/ogimage.png`,
      hasError: !parlor,
      closestParlorsByService,
      nearbyFacilities,
      discountDescription,
    },
  };
};

function ParlorId({
  defaultParlor,
  hasError,
  closestParlorsByService,
  nearbyFacilities,
  localBusinessSchema,
  discountDescription,
  pageDescription,
  publishedDate,
}: {
  defaultParlor: ParlorDetailProps;
  hasError: boolean;
  closestParlorsByService: any;
  nearbyFacilities: any;
  localBusinessSchema: any;
  discountDescription?: string;
  pageDescription: string;
  publishedDate: string | null;
}) {
  useOpenParlorPromotion50Modal();

  const router = useRouter();

  /*
   * 광고 유입 마킹. 광고 파라미터는 새로고침·페이지 내 이동에서 사라지고 `/login` 까지
   * 따라가지 않으므로, 첫 진입에 세션에 남겨야 한다.
   *
   * 여기가 없으면 광고로 들어와 이 페이지의 빠른폼으로 제출한 사람이 `/login` 에서 전부
   * 오가닉으로 분류돼 #348 모수에 섞인다. 광고와 오가닉을 두 실험으로 나눈 이유가 기저
   * 전환율 차이(12.6% 대 33.9%, #342·#343)인데 그 분리가 이 경로에서 새고 있었다.
   *
   * 컨테이너가 아니라 페이지에 두는 것은 `isSafeParlor` 로 컨테이너가 둘로 갈리기 때문이다.
   */
  useEffect(() => {
    if (!router.isReady) return;
    markParlorRequestAdEntry(router.query);
  }, [router.isReady, router.query]);

  if (hasError) return <NotFoundPage />;
  const { parlorFaqList, howtoRichSearch, carouselRichSearch, reviewResult } =
    richSearchData.parlor;

  const isSafeParlor = safeParlorIds.includes(Number(defaultParlor.id));
  const modifiedDate = defaultParlor.esky_price?.collected_at ?? null;
  const priceFaqs = isPriceDisclosureParlor(defaultParlor)
    ? getParlorPriceDisclosure(defaultParlor).faqs
    : null;

  return (
    <>
      <Head>
        <meta
          property="og:url"
          content={`https://www.goifuneral.co.kr/parlor/${defaultParlor.id}/`}
        />
        <link
          rel="canonical"
          href={`https://www.goifuneral.co.kr/parlor/${defaultParlor.id}/`}
          key="canonical"
        />
        {/*
          게시일은 이 장례식장 URL 이 처음 생긴 날(git 이력), 수정일은 e하늘 가격을 받은 날.
          빌드 날짜는 쓰지 않는다 (매 빌드 전 페이지가 같은 날짜가 된다)
        */}
        {publishedDate && (
          <meta property="article:published_time" content={publishedDate} key="published_time" />
        )}
        {modifiedDate && (
          <meta property="article:modified_time" content={modifiedDate} key="modified_time" />
        )}
      </Head>
      {(publishedDate || modifiedDate) && (
        <RichSearch
          key="webpage"
          data={{
            "@context": "https://schema.org",
            "@type": "WebPage",
            url: `https://www.goifuneral.co.kr/parlor/${defaultParlor.id}/`,
            name: `${defaultParlor.companyname} 정보, 비용`,
            ...(publishedDate ? { datePublished: publishedDate } : {}),
            ...(modifiedDate ? { dateModified: modifiedDate } : {}),
          }}
        />
      )}
      {localBusinessSchema && <RichSearch key="local-business" data={localBusinessSchema} />}
      {priceFaqs ? (
        priceFaqs.length > 0 && <RichSearch key="faqs" data={toFaqPageJsonLd(priceFaqs)} />
      ) : (
        <RichSearch key="faqs" data={parlorFaqList} />
      )}
      {isSafeParlor && (
        <>
          <RichSearch key="howto" data={howtoRichSearch(defaultParlor)} />
          <RichSearch key="carousel" data={carouselRichSearch(defaultParlor)} />
          <RichSearch key="review" data={reviewResult} />
        </>
      )}

      {isSafeParlor ? (
        <ParlorDetailContainerMayVersion
          isExcluded={excludeParlors.includes(String(defaultParlor.id))}
          pageDescription={pageDescription}
          parlor={defaultParlor}
          refetch={null}
          closestParlorsByService={closestParlorsByService}
          nearbyFacilities={nearbyFacilities}
          discountDescription={discountDescription}
        />
      ) : (
        <ParlorDetailTableVersionContainer
          isExcluded={excludeParlors.includes(String(defaultParlor.id))}
          pageDescription={pageDescription}
          parlor={defaultParlor}
          refetch={null}
          closestParlorsByService={closestParlorsByService}
          nearbyFacilities={nearbyFacilities}
          discountDescription={discountDescription}
        />
      )}

      {/* <ParlorDetailContainer
        isExcluded={excludeParlors.includes(String(defaultParlor.id))}
        pageDescription={pageDescription}
        parlor={defaultParlor}
        refetch={null}
        closestParlorsByService={closestParlorsByService}
        discountDescription={discountDescription}
      /> */}
      {/* <Footer /> */}
      {!isSafeParlor && (
        <>
          <HLine height="1px" color={colors.gray200} />
          <RenewalFooter />
        </>
      )}
    </>
  );
}

// function ParlorId({
//   defaultParlor,
//   hasError,
// }: {
//   defaultParlor: ParlorDetailProps;
//   hasError: boolean;
// }) {
//   const { data: parlor, refetch } = useQuery(
//     ["parlor", defaultParlor?.id],
//     () => getParlorDetail(defaultParlor?.id),
//     {
//       refetchOnMount: false,
//       refetchOnWindowFocus: false,
//       refetchOnReconnect: false,
//     },
//   );

//   const parlorNameFromConstant = parlors.find(
//     (parlor) => parlor.id === parseInt(defaultParlor?.id as string),
//   )?.companyname;

//   if (hasError) return <NotFoundPage />;

//   return (
//     <>
//       <Head>
//         <title>{parlorNameFromConstant} 비용, 정보, 후기 - [장례를 바로잡다, 고이]</title>
//       </Head>
//       {parlor ? (
//         <ParlorDetailContainer parlor={parlor} refetch={refetch} />
//       ) : (
//         <ParlorDetailSkeleton />
//       )}
//     </>
//   );
// }

export default ParlorId;
